'use client';
import React, { useRef, useState, useEffect } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

export default function TermsModal({
  title,
  content,
  onClose,
}: {
  title: string;
  content: string;
  onClose: () => void;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({
    canScrollUp: false,
    canScrollDown: false
  });

  // Check scroll possibility
  const checkScrollability = () => {
    if (!contentRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    const canScrollUp = scrollTop > 0;
    const canScrollDown = scrollTop < scrollHeight - clientHeight - 5; // -5 for threshold
    
    setScrollState({ canScrollUp, canScrollDown });
  };

  // Initialize and add scroll event listener
  useEffect(() => {
    const contentElement = contentRef.current;
    if (contentElement) {
      checkScrollability();
      contentElement.addEventListener('scroll', checkScrollability);
      
      // Also check when content might change
      const resizeObserver = new ResizeObserver(checkScrollability);
      resizeObserver.observe(contentElement);
      
      return () => {
        contentElement.removeEventListener('scroll', checkScrollability);
        resizeObserver.disconnect();
      };
    }
  }, []);

  // Scroll handlers
  const scrollUp = () => {
    if (!contentRef.current) return;
    contentRef.current.scrollBy({ top: -200, behavior: 'smooth' });
  };

  const scrollDown = () => {
    if (!contentRef.current) return;
    contentRef.current.scrollBy({ top: 200, behavior: 'smooth' });
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-modal-title"
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full p-8 relative border border-gray-200">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold"
          aria-label="Close terms and conditions modal"
        >
          ×
        </button>

        {/* Title */}
        <h2 id="terms-modal-title" className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">{title}</h2>

        {/* Scrollable content area */}
        <div
          ref={contentRef}
          className="max-h-[60vh] overflow-y-auto text-gray-800 pr-2 pb-10"
          dangerouslySetInnerHTML={{ __html: content }}
        />
        
        {/* Scroll indicator buttons */}
        <div className="absolute bottom-4 right-4 flex flex-col bg-white rounded-full shadow-md">
          <button 
            type="button"
            onClick={scrollUp}
            disabled={!scrollState.canScrollUp}
            className={`p-2 rounded-t-full flex items-center justify-center transition-colors ${
              scrollState.canScrollUp 
                ? 'text-blue-600 hover:bg-blue-50' 
                : 'text-gray-300 cursor-not-allowed'
            }`}
            aria-label="Scroll up"
          >
            <ChevronUp size={20} />
          </button>
          <div className="h-px bg-gray-200 w-full"></div>
          <button 
            type="button"
            onClick={scrollDown}
            disabled={!scrollState.canScrollDown}
            className={`p-2 rounded-b-full flex items-center justify-center transition-colors ${
              scrollState.canScrollDown 
                ? 'text-blue-600 hover:bg-blue-50' 
                : 'text-gray-300 cursor-not-allowed'
            }`}
            aria-label="Scroll down"
          >
            <ChevronDown size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}